import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation } from '../utils';

export default function TrustBarSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'simple';
    const tp = s.dark;

    if (items.length === 0) return null;

    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle };

    /* ── with-stats ── */
    if (variant === 'with-stats') {
        const cols = items.length <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4';
        return (
            <section className="relative" style={bgS}>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-12 ${anim}`}>
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i}
                                className={`flex flex-col items-center text-center p-6 rounded-xl transition-all duration-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{
                                    transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                    backgroundColor: tp ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                                }}>
                                <span className="text-3xl mb-3">{item.icon}</span>
                                <span className="text-sm font-semibold mb-1" style={{ color: s.text }}>{item.text}</span>
                                {item.stat && (
                                    <span className="font-bold mt-1" style={{ color: s.accent, fontSize: '2rem', lineHeight: 1.2 }}>{item.stat}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    /* ── scrolling (marquee) ── */
    if (variant === 'scrolling') {
        const uid = `tb-${block.id || '0'}`;
        const speed = config.speed === 'fast' ? 15 : config.speed === 'slow' ? 40 : 25;
        return (
            <section className="relative overflow-hidden" style={bgS}>
                <style>{`
                    @keyframes ${uid}-marquee { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
                    .${uid}-track { display:flex; gap:2rem; animation:${uid}-marquee ${speed}s linear infinite; width:max-content; }
                    .${uid}-track:hover { animation-play-state:paused; }
                `}</style>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-6 ${anim}`}>
                    <div className="overflow-hidden">
                        <div className={`${uid}-track`}>
                            {[...items, ...items].map((item, i) => (
                                <div key={i} className="flex items-center gap-2.5 shrink-0">
                                    <span className="text-2xl">{item.icon}</span>
                                    <span className="text-sm font-semibold whitespace-nowrap" style={{ color: s.text }}>{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    /* ── simple (default) ── */
    return (
        <section className="relative" style={bgS}>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-6 ${anim}`}>
                <div className="flex flex-wrap justify-center gap-8 md:gap-12">
                    {items.map((item, i) => (
                        <div key={i} className={`flex items-center gap-2.5 transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                            style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}>
                            <span className="text-2xl">{item.icon}</span>
                            <span className="text-sm font-semibold" style={{ color: s.text }}>{item.text}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
